import { RpcProvider } from 'starknet';
import { createAsyncAtom } from '../lib';
import rpcProviderAtom, { rpcProviders, getProviderUrl } from './rpcProviderAtom';

export type RpcHealthStatus = {
  isHealthy: boolean;
  lastCheckTime: number;
  lastSuccessTime: number | null;
  consecutiveFailures: number;
  latestBlockNumber: number | null;
  error: string | null;
};

const RPC_HEALTH_CHECK_INTERVAL = 30 * 1000; // 30 seconds
const RPC_UNHEALTHY_THRESHOLD = 3; // Consider unhealthy after 3 consecutive failures
const RPC_STALE_THRESHOLD = 5 * 60 * 1000; // Consider stale if no success in 5 minutes

let healthCheckState: RpcHealthStatus = {
  isHealthy: true,
  lastCheckTime: Date.now(),
  lastSuccessTime: Date.now(),
  consecutiveFailures: 0,
  latestBlockNumber: null,
  error: null,
};
let switchingProviderPromise: Promise<boolean> | null = null;

async function switchToNextHealthyProvider(reason: string): Promise<boolean> {
  if (switchingProviderPromise) {
    return switchingProviderPromise;
  }

  switchingProviderPromise = (async () => {
  const currentProvider = rpcProviderAtom.getResolvedValue();

  for (const candidate of rpcProviders) {
    if (candidate === currentProvider) continue;

    try {
      await candidate.getBlockLatestAccepted();
      console.warn(`RPC failover (${reason}): switching to`, getProviderUrl(candidate));
      rpcProviderAtom.setValue(candidate);

      // Reset health state for the newly selected provider.
      healthCheckState = {
        isHealthy: true,
        lastCheckTime: Date.now(),
        lastSuccessTime: Date.now(),
        consecutiveFailures: 0,
        latestBlockNumber: healthCheckState.latestBlockNumber,
        error: null,
      };
      rpcHealthAtom.setValue(healthCheckState);
      return true;
    } catch {
      // Candidate failed as well, continue trying the next one.
    }
  }

  return false;
  })();

  try {
    return await switchingProviderPromise;
  } finally {
    switchingProviderPromise = null;
  }
}

/**
 * Performs a health check on the RPC provider by fetching the latest block.
 * Returns the updated health status.
 */
async function checkRpcHealth(provider: RpcProvider): Promise<RpcHealthStatus> {
  const now = Date.now();

  try {
    const latestBlock = await provider.getBlockLatestAccepted();

    healthCheckState = {
      isHealthy: true,
      lastCheckTime: now,
      lastSuccessTime: now,
      consecutiveFailures: 0,
      latestBlockNumber: latestBlock.block_number,
      error: null,
    };
  } catch (error) {
    const consecutiveFailures = healthCheckState.consecutiveFailures + 1;
    const isHealthy = consecutiveFailures < RPC_UNHEALTHY_THRESHOLD;

    // Check if we haven't had a successful connection in a while
    const timeSinceLastSuccess = healthCheckState.lastSuccessTime
      ? now - healthCheckState.lastSuccessTime
      : Infinity;
    const isStale = timeSinceLastSuccess > RPC_STALE_THRESHOLD;

    healthCheckState = {
      isHealthy: isHealthy && !isStale,
      lastCheckTime: now,
      lastSuccessTime: healthCheckState.lastSuccessTime,
      consecutiveFailures,
      latestBlockNumber: healthCheckState.latestBlockNumber,
      error: error instanceof Error ? error.message : 'Unknown error',
    };

    console.warn(
      `RPC health check failed (${consecutiveFailures} consecutive failures):`,
      error instanceof Error ? error.message : error,
    );

    // When the current provider is unhealthy, try switching to the next working one.
    if (!healthCheckState.isHealthy) {
      await switchToNextHealthyProvider('health check');
    }
  }

  return healthCheckState;
}

/**
 * Atom that monitors RPC health status.
 * Polls the RPC provider every 30 seconds to check connectivity.
 */
const rpcHealthAtom = createAsyncAtom<RpcHealthStatus>(
  async get => {
    const provider = await get(rpcProviderAtom);
    return checkRpcHealth(provider);
  },
  RPC_HEALTH_CHECK_INTERVAL,
);

/**
 * Manually trigger an RPC health check.
 * Useful after a transaction fails or when connection issues are suspected.
 */
export async function triggerRpcHealthCheck(): Promise<RpcHealthStatus> {
  const provider = rpcProviderAtom.getResolvedValue();
  if (!provider) {
    return healthCheckState;
  }

  const status = await checkRpcHealth(provider);
  rpcHealthAtom.setValue(status);
  return status;
}

/**
 * Mark the RPC as unhealthy externally (e.g., when transaction receipt polling fails).
 * This allows other parts of the app to signal RPC issues.
 */
export function markRpcUnhealthy(errorMessage?: string): void {
  const now = Date.now();
  healthCheckState = {
    ...healthCheckState,
    isHealthy: false,
    lastCheckTime: now,
    consecutiveFailures: healthCheckState.consecutiveFailures + 1,
    error: errorMessage || 'RPC marked unhealthy by external signal',
  };
  rpcHealthAtom.setValue(healthCheckState);

  // Start failover immediately instead of waiting for the next health-check interval.
  void switchToNextHealthyProvider('external signal');
}

/**
 * Trigger immediate RPC failover (used when a data fetch fails).
 */
export async function triggerImmediateRpcFailover(errorMessage?: string): Promise<boolean> {
  const now = Date.now();
  healthCheckState = {
    ...healthCheckState,
    isHealthy: false,
    lastCheckTime: now,
    consecutiveFailures: healthCheckState.consecutiveFailures + 1,
    error: errorMessage || 'RPC failover requested by failed data fetch',
  };
  rpcHealthAtom.setValue(healthCheckState);

  return switchToNextHealthyProvider('immediate fetch failure');
}

/**
 * Get the current health status without triggering a new check.
 */
export function getRpcHealthStatus(): RpcHealthStatus {
  return healthCheckState;
}

export default rpcHealthAtom;
